import { CacheProvider } from '@emotion/react'
import React from 'react'
import { Helmet, HelmetProvider } from '@dr.pogodin/react-helmet'
import { createBrowserRouter, RouterProvider } from 'react-router'

import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import './i18n'
import { anonymousRoutes, authRoutes } from './routes'
import { financeLabTheme } from './theme/financeLabTheme.js'

import createEmotionCache from './utils/createEmotionCache'

import { getDateFnsLocale } from './i18n/localeUtils'
import { resolveLanguage } from './i18n/languagePreference'
import { useTranslation } from 'react-i18next'
import { AuthProvider } from './contexts/CognitoContext'
import './App.css'
import { Provider as JotaiProvider } from 'jotai'
import { CacheWarmup } from './components/CacheWarmup'
import useAuth from './hooks/useAuth'
import { UpdatesSubscriptionsProvider } from './contexts/UpdatesSubscription'
import { LayoutProvider } from './contexts/LayoutContext'
import { UserMeProvider } from './contexts/UserMeContext'
import { SearchHistoryProvider } from './contexts/SearchHistoryContext'
import { LanguagePreferenceSync } from './components/LanguagePreferenceSync'

const clientSideEmotionCache = createEmotionCache()

function FinLabRouter() {
    const { isAuthenticated, isInitialized } = useAuth()

    if (!isInitialized && !isAuthenticated) {
        return <div />
    }

    if (!isAuthenticated) {
        const anonRouteList = createBrowserRouter(anonymousRoutes)
        return <RouterProvider router={anonRouteList} />
    }

    const authRouteList = createBrowserRouter([...authRoutes, ...anonymousRoutes])

    return (
        <CacheWarmup>
            <RouterProvider router={authRouteList} />
        </CacheWarmup>
    )
}

function App({ emotionCache = clientSideEmotionCache }) {
    const { t, i18n } = useTranslation()
    const locale = resolveLanguage(i18n.resolvedLanguage ?? i18n.language)
    const adapterLocale = getDateFnsLocale(locale)

    return (
        <LayoutProvider>
            <CacheProvider value={emotionCache}>
                <HelmetProvider>
                    <Helmet titleTemplate={`%s | ${t('site.title')}`} defaultTitle={t('site.title')} />
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
                        <MuiThemeProvider theme={financeLabTheme}>
                            <AuthProvider>
                                <UpdatesSubscriptionsProvider>
                                    <JotaiProvider>
                                        <UserMeProvider>
                                            <LanguagePreferenceSync />
                                            <SearchHistoryProvider>
                                                <FinLabRouter />
                                            </SearchHistoryProvider>
                                        </UserMeProvider>
                                    </JotaiProvider>
                                </UpdatesSubscriptionsProvider>
                            </AuthProvider>
                        </MuiThemeProvider>
                    </LocalizationProvider>
                </HelmetProvider>
            </CacheProvider>
        </LayoutProvider>
    )
}

export default App
